import type { SessionSlot } from '@features/customization-session/types';

import { Zap, Undo2 } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { StatusLabel } from '@shared/components/status-label';
import { MediaThumbnails } from '@shared/components/media-preview';

type Props = {
  slot: SessionSlot;
  /** Whether this step is done for the product. */
  checked: boolean;
  /** No checkbox when the step is not to be validated product by product. */
  onToggle?: () => void;
  /** Takes the product out of the session. */
  onRemove?: () => void;
  /** Fetches the PDF of one of the personalizations of the product. */
  onFetchPdf?: (customization: number) => void;
  disabled?: boolean;
  /** Shows the visuals of the product (thumbnails, full size on click). */
  showVisuals?: boolean;
};

/** The place of the product on the jig, big: it is what the operator has to read. */
export const SlotNumber = ({ number }: { number: number }) => (
  <div
    aria-label={`Emplacement ${number} du gabarit`}
    className="flex h-12 min-w-12 shrink-0 items-center justify-center rounded-lg bg-primary px-2 text-lg font-bold text-primary-foreground sm:text-[19px]"
  >
    {number}
  </div>
);

/** A product of the session: its jig number, its name (what tells apart two products of a family), its order. */
export const SlotRow = ({
  slot,
  checked,
  onToggle,
  onRemove,
  onFetchPdf,
  disabled = false,
  showVisuals = false,
}: Props) => (
  <div
    aria-checked={onToggle ? checked : undefined}
    aria-disabled={disabled}
    className={cn(
      'flex items-center gap-4 rounded-xl border px-4 py-2 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary',
      checked ? 'border-green-500 bg-green-500/8' : 'border-border bg-card',
      onToggle && 'cursor-pointer hover:bg-accent',
      disabled && 'pointer-events-none opacity-50'
    )}
    onClick={onToggle}
    onKeyDown={
      onToggle
        ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onToggle();
            }
          }
        : undefined
    }
    role={onToggle ? 'checkbox' : undefined}
    tabIndex={onToggle && !disabled ? 0 : undefined}
  >
    <SlotNumber number={slot.number} />
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="text-base font-bold">{slot.product}</span>
      <span className="truncate text-xs text-muted-foreground">{slot.listing}</span>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-sm">{slot.order}</span>
        {slot.express && (
          <StatusLabel color="error" startIcon={<Zap />} variant="soft">
            Express
          </StatusLabel>
        )}
      </div>
      {showVisuals && (
        <MediaThumbnails className="mt-2" medias={slot.medias} size={80} title={`${slot.number} · ${slot.product}`} />
      )}
    </div>
    {onRemove && (
      <Button
        aria-label={`Retirer le produit ${slot.number} de la session`}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        size="icon"
        variant="ghost"
      >
        <Undo2 />
      </Button>
    )}
    {onToggle && <Checkbox checked={checked} className="pointer-events-none size-6" tabIndex={-1} />}
  </div>
);
